import { useMutation } from '@apollo/client/react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@optools/components/ui/alert-dialog';
import { Button } from '@optools/components/ui/button';
import { Card } from '@optools/components/ui/card';
import { Checkbox } from '@optools/components/ui/checkbox';
import { Label } from '@optools/components/ui/label';
import { DELETE_BROADCAST_SOURCE_OVERRIDE_BY_CALLSIGN } from '@optools/services/operator-gateway/btc/mutations';
import {
  BroadcastSourceOverride,
  BroadcastSourceOverridesQuery,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { Trash } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { formatProgramSourceValue } from '../utils/broadcast-source-overrides';

export interface DeleteBroadcastSourceOverrideProps {
  broadcastSourceOverride: BroadcastSourceOverride;
}

export function DeleteBroadcastSourceOverride({
  broadcastSourceOverride,
}: DeleteBroadcastSourceOverrideProps) {
  const [open, setIsOpen] = useState(false);
  const [deleteAllRegions, setDeleteAllRegions] = useState(false);

  const { callSign, gameSource, id, postgameSource, pregameSource, region } =
    broadcastSourceOverride;

  const [deleteBroadcastSourceOverride, { loading }] = useMutation(
    DELETE_BROADCAST_SOURCE_OVERRIDE_BY_CALLSIGN,
    {
      onCompleted: (data, options) => {
        const title = 'Delete Broadcast Source Override';
        const deletedCallSign = options?.variables?.callSign;
        const deletedRegion = options?.variables?.region;
        if (!data?.deleteBroadcastSourceOverrideByCallSign) {
          toast.error(title, {
            description: `Failed to delete broadcast source override for call sign: ${deletedCallSign}`,
            duration: Infinity,
          });
          return;
        }

        toast.success(title, {
          description: deletedRegion
            ? `Successfully deleted ${deletedRegion} broadcast source override for call sign: ${deletedCallSign}`
            : `Successfully deleted broadcast source override for call sign: ${deletedCallSign} in both regions`,
        });
        setIsOpen(false);
      },
      update: (cache, { data }, { variables }) => {
        if (!data?.deleteBroadcastSourceOverrideByCallSign || !variables) return;

        const { callSign, region } = variables;

        cache.modify<BroadcastSourceOverridesQuery>({
          fields: {
            broadcastSourceOverrides(existing, { readField }) {
              return existing.filter((item) => {
                if (callSign !== readField('callSign', item)) return true;
                // region omitted means delete every region for this call sign
                return region != null && region !== readField('region', item);
              });
            },
          },
        });
      },
    },
  );

  const onDelete = () =>
    deleteBroadcastSourceOverride({
      variables: { callSign, ...(!deleteAllRegions && { region }) },
    });

  return (
    <AlertDialog open={open} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <Button aria-label="delete broadcast source override" size="icon" variant="destructive">
          <Trash />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogTitle>Delete Broadcast Source Override</AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you wish to delete the following broadcast source override?
        </AlertDialogDescription>
        <Card className="flex flex-col flex-wrap gap-2 p-2 text-sm">
          <div className="flex gap-3">
            <dl className="flex gap-1">
              <dt className="font-semibold">ID:</dt>
              <dd>{id}</dd>
            </dl>
            <dl className="flex gap-1">
              <dt className="font-semibold">Call Sign:</dt>
              <dd>{callSign}</dd>
            </dl>
          </div>
          <dl className="flex gap-1">
            <dt className="font-semibold">Region:</dt>
            <dd>{region}</dd>
          </dl>
          <dl className="flex gap-1">
            <dt className="font-semibold">Pre-Game Source:</dt>
            <dd className="capitalize">{formatProgramSourceValue(pregameSource)}</dd>
          </dl>
          <dl className="flex gap-1">
            <dt className="font-semibold">Game Source:</dt>
            <dd className="capitalize">{formatProgramSourceValue(gameSource)}</dd>
          </dl>
          <dl className="flex gap-1">
            <dt className="font-semibold">Post-Game Source:</dt>
            <dd className="capitalize">{formatProgramSourceValue(postgameSource)}</dd>
          </dl>
        </Card>
        <Label className="cursor-pointer">
          <Checkbox
            checked={deleteAllRegions}
            disabled={loading}
            onCheckedChange={(checked) => setDeleteAllRegions(checked === true)}
          />
          Delete override for ALL regions
        </Label>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <Button disabled={loading} loading={loading} variant="destructive" onClick={onDelete}>
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
